import type { PreviewFile, WechatDocumentFileType } from './types'

type WechatDocumentFile = Pick<PreviewFile, 'name' | 'url'> & Partial<Pick<PreviewFile, 'type'>>

const INVALID_WECHAT_FILE_NAME_CHARS = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|'])

function sanitizeWechatFileName(value: string): string {
    return [...value]
        .map(char => {
            const code = char.codePointAt(0) ?? 0
            return code < 32 || INVALID_WECHAT_FILE_NAME_CHARS.has(char) ? '_' : char
        })
        .join('')
}

const WECHAT_FILE_NAME_MAX_LENGTH = 120

export function getWechatFileExtension(file: Pick<PreviewFile, 'name' | 'url'>): string | undefined {
    const filePaths = [file.name, file.url].filter(Boolean)
    for (const filePath of filePaths) {
        const extension = /\.([\da-z]+)(?:[#?]|$)/iu.exec(filePath)?.[1]?.toLocaleLowerCase()
        if (extension) {
            return extension
        }
    }
    return undefined
}

function safeDecodeURIComponent(value: string): string {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

/**
 * 微信预览/转发用文件名。
 * wx.openDocument 和转发菜单读本地路径上的名字，不是业务展示名。
 */
export function resolveWechatShareFileName(file: WechatDocumentFile, fileType?: WechatDocumentFileType): string {
    const extension = fileType ?? getWechatFileExtension(file)
    const rawName = file.name?.trim() || '文件'
    let name = sanitizeWechatFileName(safeDecodeURIComponent(rawName)).trim() || '文件'
    const ext = extension ? `.${extension}` : ''

    if (ext && !name.toLocaleLowerCase().endsWith(ext.toLocaleLowerCase())) {
        name = `${name}${ext}`
    }

    if (name.length > WECHAT_FILE_NAME_MAX_LENGTH && ext) {
        name = `${name.slice(0, WECHAT_FILE_NAME_MAX_LENGTH - ext.length)}${ext}`
    } else if (name.length > WECHAT_FILE_NAME_MAX_LENGTH) {
        name = name.slice(0, WECHAT_FILE_NAME_MAX_LENGTH)
    }

    return name
}
